/**
 * Que seção do painel de Configurações a URL pode abrir, para quem (GEN-3497, review r5).
 *
 * A tela de Configurações acumula duas coisas diferentes: o AUTOATENDIMENTO ("quem eu sou" —
 * meu cadastro, minha senha, minha aparência, minhas notificações) e a ADMINISTRAÇÃO do
 * cliente ("como este cliente é configurado" — recorrência, quais consultas existem, régua do
 * "A Vencer"). A barra lateral do painel sempre soube separar as duas: os itens pessoais
 * aparecem para todos, e o bloco "Configurações de produtos" fica atrás de `config.read`.
 *
 * O que faltava era aplicar o mesmo recorte ao CONTEÚDO. Enquanto a rota exigia `config.read`
 * para entrar, ninguém sem a permissão chegava perto — mas isso também deixava um colaborador
 * operacional sem NENHUMA forma de trocar a própria senha, porque a barra global fez desta
 * tela o único caminho até ela. Com a rota aberta, a URL passa a alcançar o conteúdo direto, e
 * `?secao=certificate` não pode montar a administração para quem não administra.
 *
 * Lógica pura de propósito: é uma regra de autorização, e regra de autorização merece teste
 * que não dependa de montar catorze seções de tela.
 */

/** Seção padrão — a mesma que a tela abre quando a URL não pede nada. */
export const SECAO_PADRAO = "account";

/** Seções que respondem "quem eu sou". Qualquer pessoa com acesso ao cliente pode abrir. */
export const SECOES_DE_AUTOATENDIMENTO = ["account", "changePassword", "display", "notification"] as const;

export type SecaoDeAutoatendimento = (typeof SECOES_DE_AUTOATENDIMENTO)[number];

export const ehAutoatendimento = (secao: string): secao is SecaoDeAutoatendimento =>
  (SECOES_DE_AUTOATENDIMENTO as readonly string[]).includes(secao);

/**
 * Seções que existiram e foram retiradas do produto. Ficam listadas de propósito: o link antigo
 * continua circulando (favorito, e-mail, print de suporte) e, sem isto, ele abriria o painel com
 * a área de conteúdo em branco — o pior desfecho possível, porque parece defeito e não mudança.
 *
 * `company` era a tela dos campos personalizados de empresa (Campo 1/2/3), removida na GEN-3642:
 * substituída pelos campos personalizados da Base Cadastral, que têm tipo, ordem e permissão
 * próprios.
 */
const SECOES_APOSENTADAS = ["company"] as const;

/**
 * GEN-4114 — o Portal de autosserviço virou um GRUPO do menu com subpáginas. "Pedidos" é a fila do time tributário
 * (`cnd.write` + produto portal); as demais são a configuração da organização (`config.read`, como hoje). A chave
 * `portal` continua existindo e passou a ser a subpágina "Acessos" (status, domínios, grupos, política, quem acessa).
 */
export const SECOES_DO_PORTAL_CONFIG = [
  "portal",
  "portalForm",
  "portalBranding",
  "portalIntegrations",
  "portalTrail",
] as const;

export const ehSecaoDoPortalConfig = (secao: string): boolean =>
  (SECOES_DO_PORTAL_CONFIG as readonly string[]).includes(secao);

/**
 * Devolve a seção que de fato pode ser aberta. Quem administra o cliente abre qualquer uma;
 * quem não administra cai na seção padrão em vez de ver a administração.
 */
export const resolverSecao = (
  secaoPedida: string | null | undefined,
  podeAdministrar: boolean,
  // GEN-3670 (r8): "Consumo do contrato" tem permissão própria (`clientUsage.read`) — a barra
  // lateral já o mostra para quem a tem, mesmo sem administrar. Com a seção no endereço, recarregar
  // `/configuracoes/consumo-do-contrato` não pode jogar essa pessoa para "Minha conta".
  podeVerConsumo = false,
  // GEN-3820: "Pedidos do portal" é a fila de trabalho do time tributário — gate `cnd.write`, o mesmo do
  // backend (`/portal/:guid_client/requests`), e não `config.read`: quem atende os pedidos não precisa
  // administrar o cliente, e o admin sem `cnd.write` levaria 403 numa tela vazia.
  podeVerPedidosPortal = false,
  // GEN-4262: o Portal de Autosserviço só existe para cliente liberado pela Taxly (`products.portal_released`).
  // Sem a liberação, nenhuma seção do grupo abre — nem pelo endereço digitado.
  portalLiberado = false
): string => {
  const secao = secaoPedida ?? SECAO_PADRAO;
  if ((SECOES_APOSENTADAS as readonly string[]).includes(secao)) return SECAO_PADRAO;
  if (!portalLiberado && (secao === "portalRequests" || ehSecaoDoPortalConfig(secao))) return SECAO_PADRAO;
  if (secao === "portalRequests") return podeVerPedidosPortal ? secao : SECAO_PADRAO;
  // GEN-4114: quem atende pedidos mas não administra e abre um endereço da configuração do portal (o link antigo
  // `/configuracoes/portal-de-autosservico`, por exemplo) cai na fila de pedidos — o que ele alcança no mesmo grupo —
  // em vez de "Minha conta".
  if (!podeAdministrar && ehSecaoDoPortalConfig(secao) && podeVerPedidosPortal) return "portalRequests";
  if (podeAdministrar) return secao;
  if (secao === "consumption" && podeVerConsumo) return secao;
  return ehAutoatendimento(secao) ? secao : SECAO_PADRAO;
};

/**
 * GEN-3670 (r8) — cada seção tem endereço próprio: `/configuracoes/base-cadastral`. Antes a URL
 * ficava em `/configuracoes` e a seção vivia só no estado da tela (link, favorito e Voltar não
 * levavam de volta para ela). O caminho usa o nome da tela, não a chave interna; `?secao=` antigo
 * continua aceito e é convertido para o caminho.
 */
export const SLUG_POR_SECAO: Record<string, string> = {
  account: "minha-conta",
  changePassword: "trocar-senha",
  display: "exibicao",
  notification: "notificacoes",
  certificate: "certidoes",
  taxCurrentAccount: "conta-corrente",
  taxKanban: "tax-kanban",
  DTE: "caixa-postal",
  baseCadastral: "base-cadastral",
  automations: "automacoes",
  consultas: "consultas",
  certificateCustomField: "campos-personalizados-certidoes",
  taxKanbanCustomField: "campos-personalizados-atividades",
  dueTerm: "regras-de-vencimento",
  // GEN-4114: subpáginas do grupo "Portal de autosserviço".
  portalRequests: "portal-pedidos",
  portal: "portal-acessos",
  portalForm: "portal-formulario",
  portalBranding: "portal-aparencia",
  portalIntegrations: "portal-integracoes",
  portalTrail: "portal-trilha",
  files: "arquivos",
};

/**
 * Endereços que existiram e mudaram de nome. Continuam abrindo a seção (favorito, e-mail, link do sino) e o painel
 * troca o endereço pelo atual.
 */
const SLUGS_ANTIGOS: Record<string, string> = {
  // GEN-4114: antes da divisão em subpáginas.
  "portal-de-autosservico": "portal",
  "pedidos-do-portal": "portalRequests",
};

const SECAO_POR_SLUG: Record<string, string> = {
  ...SLUGS_ANTIGOS,
  ...Object.fromEntries(Object.entries(SLUG_POR_SECAO).map(([secao, slug]) => [slug, secao])),
};

/** A seção que o trecho da URL pede. Aceita também a chave interna (`/configuracoes/baseCadastral`). */
export const secaoDoSlug = (slug?: string | null): string | undefined => {
  if (!slug) return undefined;
  return SECAO_POR_SLUG[slug] ?? (slug in SLUG_POR_SECAO ? slug : undefined);
};

/** O endereço de uma seção. */
export const caminhoDaSecao = (secao: string): string => `/configuracoes/${SLUG_POR_SECAO[secao] ?? secao}`;
